<template>
    <v-row no-gutters class="fill-height">
        <v-col cols="12">
            <v-row>
                <v-col cols="12">
                    <v-card class="pa-6 rounded-lg" elevation="0" color="surface">
                        <v-row align="center" justify="space-between" class="mb-4">
                            <v-col cols="auto">
                                <h1 id="tour-reschedule-title" class="text-h4 primary--text font-weight-bold">
                                    <v-icon large left color="primary">mdi-calendar-refresh</v-icon>
                                    Solicitações de Reagendamento
                                </h1>
                            </v-col>
                            <v-col cols="auto">
                                <v-chip id="tour-status-chip" v-if="totalPendingCount > 0" color="secondary" class="white--text font-weight-bold">
                                    {{ totalPendingCount }} pendente{{ totalPendingCount > 1 ? 's' : '' }}
                                </v-chip>
                                <v-chip v-else color="success" class="white--text font-weight-bold">
                                    <v-icon left small>mdi-check-circle</v-icon>
                                    Nenhuma solicitação pendente
                                </v-chip>
                            </v-col>
                        </v-row>

                        <v-tabs id="tour-tabs" v-model="currentTab" background-color="transparent" color="primary" class="mb-4">
                            <v-tab>
                                <v-icon left>mdi-clock-outline</v-icon>
                                Pendentes
                                <v-chip v-if="pendingRequests.length" small class="ml-2" color="secondary">
                                    {{ pendingRequests.length }}
                                </v-chip>
                            </v-tab>
                            <v-tab id="tour-accepted-tab">
                                <v-icon left>mdi-check-circle</v-icon>
                                Aceitas
                                <v-chip v-if="acceptedRequests.length" small class="ml-2" color="success">
                                    {{ acceptedRequests.length }}
                                </v-chip>
                            </v-tab>
                            <v-tab id="tour-rejected-tab">
                                <v-icon left>mdi-close-circle</v-icon>
                                Recusadas
                                <v-chip v-if="rejectedRequests.length" small class="ml-2" color="error">
                                    {{ rejectedRequests.length }}
                                </v-chip>
                            </v-tab>
                            <v-tab id="tour-counterproposals-tab">
                                <v-icon left>mdi-calendar-edit</v-icon>
                                Contrapropostas
                                <v-chip v-if="proposedRequests.length" small class="ml-2" color="primary">
                                    {{ proposedRequests.length }}
                                </v-chip>
                            </v-tab>
                        </v-tabs>

                        <v-tabs-items v-model="currentTab">
                            <!-- Pendentes Tab -->
                            <v-tab-item>
                                <v-row v-if="loading" justify="center" class="pa-12">
                                    <v-col cols="auto">
                                        <v-progress-circular indeterminate color="primary" size="64" />
                                    </v-col>
                                </v-row>

                                <v-row v-else-if="!pendingRequests.length" justify="center" class="pa-12">
                                    <v-col cols="12" class="text-center">
                                        <v-icon size="120" color="secondary">mdi-calendar-check</v-icon>
                                        <p class="text-h5 mt-6 secondary--text">Nenhuma solicitação pendente</p>
                                        <p class="text-body-1 secondary--text">Quando os clientes solicitarem reagendamentos pelo app, eles aparecerão aqui.</p>
                                    </v-col>
                                </v-row>

                                <v-expansion-panels v-else accordion class="rounded-xl">
                                    <v-expansion-panel v-for="(request, index) in pendingRequests" :key="request.id" :id="index === 0 && String(request.id).startsWith('tour-fake-') ? 'tour-pending-request' : null" class="mb-3">
                                        <v-expansion-panel-header class="pa-4">
                                            <v-row no-gutters align="center">
                                                <v-col cols="12" md="3">
                                                    <div class="d-flex align-center">
                                                        <v-avatar color="primary" size="40" class="mr-3">
                                                            <v-icon color="white">mdi-account</v-icon>
                                                        </v-avatar>
                                                        <div>
                                                            <div class="font-weight-bold text-subtitle-1">{{ request.customers_session?.customer?.name || 'Cliente' }}</div>
                                                            <div class="text-caption secondary--text">{{ request.customers_session?.service?.name || 'Serviço' }}</div>
                                                        </div>
                                                    </div>
                                                </v-col>
                                                <v-col cols="12" md="3">
                                                    <div class="text-caption secondary--text">Horário Atual</div>
                                                    <div class="font-weight-medium">{{ formatDateTime(request.customers_session?.session_date) }}</div>
                                                </v-col>
                                                <v-col cols="12" md="3">
                                                    <div class="text-caption secondary--text">Horário Solicitado</div>
                                                    <div class="font-weight-medium primary--text">{{ formatDateTime(request.new_date + ' ' + request.new_time) }}</div>
                                                </v-col>
                                                <v-col cols="12" md="3">
                                                    <div class="text-caption secondary--text">Solicitado em</div>
                                                    <div class="font-weight-medium">{{ formatDateTime(request.created_at) }}</div>
                                                </v-col>
                                            </v-row>
                                        </v-expansion-panel-header>
                                        
                                        <v-expansion-panel-content>
                                            <v-card flat color="white" class="pa-4 rounded-xl">
                                                <v-row>
                                                    <v-col v-if="request.reason" cols="12">
                                                        <div class="text-subtitle-2 secondary--text mb-2">Motivo da Solicitação</div>
                                                        <v-card id="tour-request-reason" flat color="surface" class="pa-3 rounded-lg">
                                                            <p class="mb-0">{{ request.reason }}</p>
                                                        </v-card>
                                                    </v-col>

                                                    <!-- Propose New Time Section -->
                                                    <v-col v-if="proposingFor === request.id" cols="12">
                                                        <v-divider class="my-4" />
                                                        <div class="text-subtitle-2 secondary--text mb-3">Propor Novos Horários</div>

                                                        <!-- Opção 1 (obrigatória) -->
                                                        <div class="text-caption secondary--text mb-2">Opção 1 (obrigatória)</div>
                                                        <v-row>
                                                            <v-col cols="12" md="6">
                                                                <v-menu v-model="showDatePicker[0]" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                                                                    <template v-slot:activator="{ on, attrs }">
                                                                        <v-text-field :value="formatDate(proposedSlots[0].date)" label="Data" append-icon="mdi-calendar" readonly outlined v-bind="attrs" v-on="on" />
                                                                    </template>
                                                                    <v-date-picker v-model="proposedSlots[0].date" no-title scrollable @input="showDatePicker[0] = false" />
                                                                </v-menu>
                                                            </v-col>
                                                            <v-col cols="12" md="6">
                                                                <v-menu v-model="showTimePicker[0]" :close-on-content-click="false" transition="scale-transition" offset-y min-width="300px">
                                                                    <template v-slot:activator="{ on, attrs }">
                                                                        <v-text-field v-model="proposedSlots[0].time" label="Hora" append-icon="mdi-clock-outline" readonly outlined v-bind="attrs" v-on="on" />
                                                                    </template>
                                                                    <v-time-picker v-if="showTimePicker[0]" v-model="proposedSlots[0].time" format="24hr" full-width @click:minute="showTimePicker[0] = false" />
                                                                </v-menu>
                                                            </v-col>
                                                        </v-row>

                                                        <!-- Opções adicionais -->
                                                        <template v-for="i in [1, 2]">
                                                            <div v-if="i < extraSlots + 1" :key="'slot-'+i">
                                                                <v-row align="center" class="mb-0">
                                                                    <v-col cols="auto"><div class="text-caption secondary--text">Opção {{ i + 1 }} (opcional)</div></v-col>
                                                                    <v-col cols="auto">
                                                                        <v-btn icon x-small color="error" @click="removeSlot(i)"><v-icon>mdi-close-circle</v-icon></v-btn>
                                                                    </v-col>
                                                                </v-row>
                                                                <v-row>
                                                                    <v-col cols="12" md="6">
                                                                        <v-menu v-model="showDatePicker[i]" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                                                                            <template v-slot:activator="{ on, attrs }">
                                                                                <v-text-field :value="formatDate(proposedSlots[i].date)" label="Data" append-icon="mdi-calendar" readonly outlined v-bind="attrs" v-on="on" />
                                                                            </template>
                                                                            <v-date-picker v-model="proposedSlots[i].date" no-title scrollable @input="showDatePicker[i] = false" />
                                                                        </v-menu>
                                                                    </v-col>
                                                                    <v-col cols="12" md="6">
                                                                        <v-menu v-model="showTimePicker[i]" :close-on-content-click="false" transition="scale-transition" offset-y min-width="300px">
                                                                            <template v-slot:activator="{ on, attrs }">
                                                                                <v-text-field v-model="proposedSlots[i].time" label="Hora" append-icon="mdi-clock-outline" readonly outlined v-bind="attrs" v-on="on" />
                                                                            </template>
                                                                            <v-time-picker v-if="showTimePicker[i]" v-model="proposedSlots[i].time" format="24hr" full-width @click:minute="showTimePicker[i] = false" />
                                                                        </v-menu>
                                                                    </v-col>
                                                                </v-row>
                                                            </div>
                                                        </template>

                                                        <!-- Botão adicionar opção -->
                                                        <v-btn v-if="extraSlots < 2" text small color="primary" class="mb-3" @click="extraSlots++">
                                                            <v-icon left small>mdi-plus</v-icon> Adicionar opção {{ extraSlots + 2 }}
                                                        </v-btn>

                                                        <!-- Motivo da contraproposta -->
                                                        <v-textarea
                                                            v-model="counterReason"
                                                            label="Motivo da contraproposta (opcional)"
                                                            outlined
                                                            rows="2"
                                                            auto-grow
                                                            hide-details
                                                            class="mt-2"
                                                        />
                                                    </v-col>

                                                    <!-- Action Buttons -->
                                                    <v-col cols="12">
                                                        <v-divider class="my-2" />
                                                        <v-row justify="end" class="mt-4">
                                                            <v-col cols="auto">
                                                                <v-btn
                                                                    v-if="proposingFor === request.id"
                                                                    outlined
                                                                    color="secondary"
                                                                    @click="cancelPropose"
                                                                    :disabled="actionLoading"
                                                                >
                                                                    <v-icon left>mdi-close</v-icon>
                                                                    Cancelar
                                                                </v-btn>
                                                                <v-btn
                                                                    id="tour-reject-button"
                                                                    v-else
                                                                    outlined
                                                                    color="error"
                                                                    @click="rejectRequest(request.id)"
                                                                    :loading="actionLoading && currentAction === 'reject-' + request.id"
                                                                    :disabled="actionLoading"
                                                                >
                                                                    <v-icon left>mdi-close-circle</v-icon>
                                                                    Rejeitar
                                                                </v-btn>
                                                            </v-col>
                                                            <v-col cols="auto">
                                                                <v-btn
                                                                    v-if="proposingFor === request.id"
                                                                    color="primary"
                                                                    @click="submitProposal(request.id)"
                                                                    :loading="actionLoading && currentAction === 'propose-' + request.id"
                                                                    :disabled="actionLoading || !proposedSlots[0].date || !proposedSlots[0].time"
                                                                >
                                                                    <v-icon left>mdi-send</v-icon>
                                                                    Enviar Proposta
                                                                </v-btn>
                                                                <v-btn
                                                                    id="tour-propose-button"
                                                                    v-else
                                                                    outlined
                                                                    color="primary"
                                                                    @click="startPropose(request.id)"
                                                                    :disabled="actionLoading"
                                                                >
                                                                    <v-icon left>mdi-calendar-edit</v-icon>
                                                                    Propor Novo Horário
                                                                </v-btn>
                                                            </v-col>
                                                            <v-col v-if="proposingFor !== request.id" cols="auto">
                                                                <v-btn
                                                                    id="tour-approve-button"
                                                                    color="success"
                                                                    @click="approveRequest(request.id)"
                                                                    :loading="actionLoading && currentAction === 'approve-' + request.id"
                                                                    :disabled="actionLoading"
                                                                >
                                                                    <v-icon left>mdi-check-circle</v-icon>
                                                                    Aprovar
                                                                </v-btn>
                                                            </v-col>
                                                        </v-row>
                                                    </v-col>
                                                </v-row>
                                            </v-card>
                                        </v-expansion-panel-content>
                                    </v-expansion-panel>
                                </v-expansion-panels>
                            </v-tab-item>

                            <!-- Aceitas Tab -->
                            <v-tab-item>
                                <v-row v-if="loading" justify="center" class="pa-12">
                                    <v-col cols="auto">
                                        <v-progress-circular indeterminate color="primary" size="64" />
                                    </v-col>
                                </v-row>

                                <v-row v-else-if="!acceptedRequests.length" justify="center" class="pa-12">
                                    <v-col cols="12" class="text-center">
                                        <v-icon size="120" color="success">mdi-check-circle-outline</v-icon>
                                        <p class="text-h5 mt-6 secondary--text">Nenhuma solicitação aceita</p>
                                    </v-col>
                                </v-row>

                                <v-expansion-panels v-else accordion class="rounded-xl">
                                    <v-expansion-panel v-for="request in acceptedRequests" :key="request.id" class="mb-3">
                                        <v-expansion-panel-header class="pa-4">
                                            <v-row no-gutters align="center">
                                                <v-col cols="12" md="3">
                                                    <div class="d-flex align-center">
                                                        <v-avatar color="success" size="40" class="mr-3">
                                                            <v-icon color="white">mdi-account</v-icon>
                                                        </v-avatar>
                                                        <div>
                                                            <div class="font-weight-bold text-subtitle-1">{{ request.customers_session?.customer?.name || 'Cliente' }}</div>
                                                            <div class="text-caption secondary--text">{{ request.customers_session?.service?.name || 'Serviço' }}</div>
                                                        </div>
                                                    </div>
                                                </v-col>
                                                <v-col cols="12" md="3">
                                                    <div class="text-caption secondary--text">Horário Anterior</div>
                                                    <div class="font-weight-medium">{{ formatDateTime(request.customers_session?.session_date) }}</div>
                                                </v-col>
                                                <v-col cols="12" md="3">
                                                    <div class="text-caption secondary--text">Novo Horário</div>
                                                    <div class="font-weight-medium success--text">{{ formatDateTime(request.new_date + ' ' + request.new_time) }}</div>
                                                </v-col>
                                                <v-col cols="12" md="3">
                                                    <div class="text-caption secondary--text">Aceito em</div>
                                                    <div class="font-weight-medium">{{ formatDateTime(request.updated_at) }}</div>
                                                </v-col>
                                            </v-row>
                                        </v-expansion-panel-header>
                                        
                                        <v-expansion-panel-content>
                                            <v-card flat color="white" class="pa-4 rounded-xl">
                                                <v-row>
                                                    <v-col v-if="request.reason" cols="12">
                                                        <div class="text-subtitle-2 secondary--text mb-2">Motivo da Solicitação</div>
                                                        <v-card flat color="surface" class="pa-3 rounded-lg">
                                                            <p class="mb-0">{{ request.reason }}</p>
                                                        </v-card>
                                                    </v-col>
                                                </v-row>
                                            </v-card>
                                        </v-expansion-panel-content>
                                    </v-expansion-panel>
                                </v-expansion-panels>
                            </v-tab-item>

                            <!-- Recusadas Tab -->
                            <v-tab-item>
                                <v-row v-if="loading" justify="center" class="pa-12">
                                    <v-col cols="auto">
                                        <v-progress-circular indeterminate color="primary" size="64" />
                                    </v-col>
                                </v-row>

                                <v-row v-else-if="!rejectedRequests.length" justify="center" class="pa-12">
                                    <v-col cols="12" class="text-center">
                                        <v-icon size="120" color="error">mdi-close-circle-outline</v-icon>
                                        <p class="text-h5 mt-6 secondary--text">Nenhuma solicitação recusada</p>
                                    </v-col>
                                </v-row>

                                <v-expansion-panels v-else accordion class="rounded-xl">
                                    <v-expansion-panel v-for="request in rejectedRequests" :key="request.id" class="mb-3">
                                        <v-expansion-panel-header class="pa-4">
                                            <v-row no-gutters align="center">
                                                <v-col cols="12" md="3">
                                                    <div class="d-flex align-center">
                                                        <v-avatar color="error" size="40" class="mr-3">
                                                            <v-icon color="white">mdi-account</v-icon>
                                                        </v-avatar>
                                                        <div>
                                                            <div class="font-weight-bold text-subtitle-1">{{ request.customers_session?.customer?.name || 'Cliente' }}</div>
                                                            <div class="text-caption secondary--text">{{ request.customers_session?.service?.name || 'Serviço' }}</div>
                                                        </div>
                                                    </div>
                                                </v-col>
                                                <v-col cols="12" md="3">
                                                    <div class="text-caption secondary--text">Horário Atual</div>
                                                    <div class="font-weight-medium">{{ formatDateTime(request.customers_session?.session_date) }}</div>
                                                </v-col>
                                                <v-col cols="12" md="3">
                                                    <div class="text-caption secondary--text">Horário Solicitado</div>
                                                    <div class="font-weight-medium">{{ formatDateTime(request.new_date + ' ' + request.new_time) }}</div>
                                                </v-col>
                                                <v-col cols="12" md="3">
                                                    <div class="text-caption secondary--text">Recusado em</div>
                                                    <div class="font-weight-medium">{{ formatDateTime(request.updated_at) }}</div>
                                                </v-col>
                                            </v-row>
                                        </v-expansion-panel-header>
                                        
                                        <v-expansion-panel-content>
                                            <v-card flat color="white" class="pa-4 rounded-xl">
                                                <v-row>
                                                    <v-col v-if="request.reason" cols="12">
                                                        <div class="text-subtitle-2 secondary--text mb-2">Motivo da Solicitação</div>
                                                        <v-card flat color="surface" class="pa-3 rounded-lg">
                                                            <p class="mb-0">{{ request.reason }}</p>
                                                        </v-card>
                                                    </v-col>
                                                </v-row>
                                            </v-card>
                                        </v-expansion-panel-content>
                                    </v-expansion-panel>
                                </v-expansion-panels>
                            </v-tab-item>

                            <!-- Contrapropostas Tab -->
                            <v-tab-item>
                                <v-row v-if="loading" justify="center" class="pa-12">
                                    <v-col cols="auto">
                                        <v-progress-circular indeterminate color="primary" size="64" />
                                    </v-col>
                                </v-row>

                                <v-row v-else-if="!proposedRequests.length" justify="center" class="pa-12">
                                    <v-col cols="12" class="text-center">
                                        <v-icon size="120" color="primary">mdi-calendar-edit</v-icon>
                                        <p class="text-h5 mt-6 secondary--text">Nenhuma contraproposta enviada</p>
                                    </v-col>
                                </v-row>

                                <div v-else>
                                    <!-- Contrapropostas Aceitas -->
                                    <div v-if="acceptedCounterproposals.length">
                                        <v-subheader class="text-h6 success--text font-weight-bold">
                                            <v-icon left color="success">mdi-check-circle</v-icon>
                                            Contrapropostas Aceitas
                                        </v-subheader>
                                        <v-expansion-panels accordion class="rounded-xl mb-6">
                                            <v-expansion-panel v-for="request in acceptedCounterproposals" :key="request.id" class="mb-3">
                                                <v-expansion-panel-header class="pa-4">
                                                    <v-row no-gutters align="center">
                                                        <v-col cols="12" md="3">
                                                            <div class="d-flex align-center">
                                                                <v-avatar color="success" size="40" class="mr-3">
                                                                    <v-icon color="white">mdi-account</v-icon>
                                                                </v-avatar>
                                                                <div>
                                                                    <div class="font-weight-bold text-subtitle-1">{{ request.customers_session?.customer?.name || 'Cliente' }}</div>
                                                                    <div class="text-caption secondary--text">{{ request.customers_session?.service?.name || 'Serviço' }}</div>
                                                                </div>
                                                            </div>
                                                        </v-col>
                                                        <v-col cols="12" md="3">
                                                            <div class="text-caption secondary--text">Solicitação Original</div>
                                                            <div class="font-weight-medium">{{ formatDateTime(request.new_date + ' ' + request.new_time) }}</div>
                                                        </v-col>
                                                        <v-col cols="12" md="3">
                                                            <div class="text-caption secondary--text">Sua Proposta</div>
                                                            <div class="font-weight-medium success--text">{{ formatDateTime(request.proposed_date + ' ' + request.proposed_time) }}</div>
                                                        </v-col>
                                                        <v-col cols="12" md="3">
                                                            <v-chip small color="success" class="white--text">
                                                                <v-icon left small>mdi-check</v-icon>
                                                                Aceita pelo cliente
                                                            </v-chip>
                                                        </v-col>
                                                    </v-row>
                                                </v-expansion-panel-header>
                                                
                                                <v-expansion-panel-content>
                                                    <v-card flat color="white" class="pa-4 rounded-xl">
                                                        <v-row>
                                                            <v-col v-if="request.reason" cols="12">
                                                                <div class="text-subtitle-2 secondary--text mb-2">Motivo da Solicitação Original</div>
                                                                <v-card flat color="surface" class="pa-3 rounded-lg">
                                                                    <p class="mb-0">{{ request.reason }}</p>
                                                                </v-card>
                                                            </v-col>
                                                        </v-row>
                                                    </v-card>
                                                </v-expansion-panel-content>
                                            </v-expansion-panel>
                                        </v-expansion-panels>
                                    </div>

                                    <!-- Contrapropostas Pendentes/Recusadas -->
                                    <div v-if="rejectedCounterproposals.length">
                                        <v-subheader class="text-h6 warning--text font-weight-bold">
                                            <v-icon left color="warning">mdi-clock-alert</v-icon>
                                            Contrapropostas Pendentes/Recusadas
                                        </v-subheader>
                                        <v-expansion-panels accordion class="rounded-xl">
                                            <v-expansion-panel v-for="request in rejectedCounterproposals" :key="request.id" class="mb-3">
                                                <v-expansion-panel-header class="pa-4">
                                                    <v-row no-gutters align="center">
                                                        <v-col cols="12" md="3">
                                                            <div class="d-flex align-center">
                                                                <v-avatar color="warning" size="40" class="mr-3">
                                                                    <v-icon color="white">mdi-account</v-icon>
                                                                </v-avatar>
                                                                <div>
                                                                    <div class="font-weight-bold text-subtitle-1">{{ request.customers_session?.customer?.name || 'Cliente' }}</div>
                                                                    <div class="text-caption secondary--text">{{ request.customers_session?.service?.name || 'Serviço' }}</div>
                                                                </div>
                                                            </div>
                                                        </v-col>
                                                        <v-col cols="12" md="3">
                                                            <div class="text-caption secondary--text">Solicitação Original</div>
                                                            <div class="font-weight-medium">{{ formatDateTime(request.new_date + ' ' + request.new_time) }}</div>
                                                        </v-col>
                                                        <v-col cols="12" md="3">
                                                            <div class="text-caption secondary--text">Sua Proposta</div>
                                                            <div class="font-weight-medium primary--text">{{ formatDateTime(request.proposed_date + ' ' + request.proposed_time) }}</div>
                                                        </v-col>
                                                        <v-col cols="12" md="3">
                                                            <v-chip small :color="getCounterproposalStatusColor(request)" class="white--text">
                                                                <v-icon left small>{{ getCounterproposalStatusIcon(request) }}</v-icon>
                                                                {{ getCounterproposalStatusText(request) }}
                                                            </v-chip>
                                                        </v-col>
                                                    </v-row>
                                                </v-expansion-panel-header>
                                                
                                                <v-expansion-panel-content>
                                                    <v-card flat color="white" class="pa-4 rounded-xl">
                                                        <v-row>
                                                            <v-col v-if="request.reason" cols="12">
                                                                <div class="text-subtitle-2 secondary--text mb-2">Motivo da Solicitação Original</div>
                                                                <v-card flat color="surface" class="pa-3 rounded-lg">
                                                                    <p class="mb-0">{{ request.reason }}</p>
                                                                </v-card>
                                                            </v-col>
                                                        </v-row>
                                                    </v-card>
                                                </v-expansion-panel-content>
                                            </v-expansion-panel>
                                        </v-expansion-panels>
                                    </div>
                                </div>
                            </v-tab-item>
                        </v-tabs-items>
                    </v-card>
                </v-col>
            </v-row>

            <!-- Success Snackbar -->
            <v-snackbar v-model="showSuccess" color="success" timeout="3000" top>
                {{ successMessage }}
                <template v-slot:action="{ attrs }">
                    <v-btn text v-bind="attrs" @click="showSuccess = false">Fechar</v-btn>
                </template>
            </v-snackbar>

            <!-- Error Snackbar -->
            <v-snackbar v-model="showError" color="error" timeout="5000" top>
                {{ errorMessage }}
                <template v-slot:action="{ attrs }">
                    <v-btn text v-bind="attrs" @click="showError = false">Fechar</v-btn>
                </template>
            </v-snackbar>
        </v-col>
    </v-row>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import dayjs from 'dayjs';

export default {
    data() {
        return {
            currentTab: 0,
            loading: false,
            actionLoading: false,
            currentAction: null,
            proposingFor: null,
            // Multi-slot proposal state (up to 3)
            proposedSlots: [
                { date: null, time: null },
                { date: null, time: null },
                { date: null, time: null },
            ],
            extraSlots: 0,       // 0 = only option 1 shown, 1 = options 1+2, 2 = all 3
            counterReason: '',
            showDatePicker: [false, false, false],
            showTimePicker: [false, false, false],
            showSuccess: false,
            showError: false,
            successMessage: '',
            errorMessage: '',
            refreshInterval: null
        };
    },
    computed: {
        ...mapState("schedule", {
            reschedule_requests: state => state.reschedule_requests
        }),
        allRequests() {
            return Array.isArray(this.reschedule_requests) 
                ? this.reschedule_requests 
                : (this.reschedule_requests?.data || []);
        },
        pendingRequests() {
            return this.allRequests.filter(r => r.status === 'pending');
        },
        acceptedRequests() {
            return this.allRequests.filter(r => r.status === 'approved');
        },
        rejectedRequests() {
            return this.allRequests.filter(r => r.status === 'rejected');
        },
        proposedRequests() {
            return this.allRequests.filter(r => r.status === 'proposed');
        },
        acceptedCounterproposals() {
            return this.proposedRequests.filter(r => r.customers_session?.rescheduled === true);
        },
        rejectedCounterproposals() {
            return this.proposedRequests.filter(r => 
                r.customers_session?.rescheduled === false || 
                r.customers_session?.finished === true
            );
        },
        totalPendingCount() {
            return this.pendingRequests.length;
        }
    },
    mounted() {
        this.loadRequests();
        
        // Set up auto-refresh every 3 minutes
        this.refreshInterval = setInterval(() => {
            this.loadRequests();
        }, 180000);
        
        // Expose for tour
        window.$rescheduleRequestsPage = this;
    },
    beforeDestroy() {
        if (this.refreshInterval) {
            clearInterval(this.refreshInterval);
        }
        
        // Clean up global reference
        if (window.$rescheduleRequestsPage === this) {
            window.$rescheduleRequestsPage = null;
        }
    },
    methods: {
        ...mapActions("schedule", [
            "ActionDoGetRescheduleRequests",
            "ActionDoApproveRescheduleRequest",
            "ActionDoRejectRescheduleRequest",
            "ActionDoProposeRescheduleRequest",
            "ActionDoGetCalendarEvents"
        ]),
        loadRequests() {
            this.loading = true;
            this.ActionDoGetRescheduleRequests({status: ['pending', 'approved', 'rejected', 'proposed']})
                .catch(error => {
                    console.error('Error loading reschedule requests:', error);
                    this.showErrorMessage('Erro ao carregar solicitações de reagendamento');
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        approveRequest(requestId) {
            this.currentAction = 'approve-' + requestId;
            this.actionLoading = true;
            
            this.ActionDoApproveRescheduleRequest(requestId)
                .then(() => {
                    this.showSuccessMessage('Solicitação aprovada com sucesso!');
                    this.loadRequests();
                    this.ActionDoGetCalendarEvents();
                })
                .catch(error => {
                    console.error('Error approving request:', error);
                    this.showErrorMessage('Erro ao aprovar solicitação');
                })
                .finally(() => {
                    this.actionLoading = false;
                    this.currentAction = null;
                });
        },
        rejectRequest(requestId) {
            this.currentAction = 'reject-' + requestId;
            this.actionLoading = true;
            
            this.ActionDoRejectRescheduleRequest(requestId)
                .then(() => {
                    this.showSuccessMessage('Solicitação rejeitada');
                    this.loadRequests();
                })
                .catch(error => {
                    console.error('Error rejecting request:', error);
                    this.showErrorMessage('Erro ao rejeitar solicitação');
                })
                .finally(() => {
                    this.actionLoading = false;
                    this.currentAction = null;
                });
        },
        startPropose(requestId) {
            this.proposingFor = requestId;
            this.proposedSlots = [{ date: null, time: null }, { date: null, time: null }, { date: null, time: null }];
            this.extraSlots = 0;
            this.counterReason = '';
            this.showDatePicker = [false, false, false];
            this.showTimePicker = [false, false, false];
        },
        removeSlot(index) {
            this.proposedSlots[index] = { date: null, time: null };
            // If removing slot 2 (index 2) while slot 1 (index 1) still exists, keep extraSlots
            if (index === 2) this.extraSlots = Math.min(this.extraSlots, 1);
            if (index === 1) this.extraSlots = 0;
        },
        cancelPropose() {
            this.resetPropose();
        },
        resetPropose() {
            this.proposingFor = null;
            this.proposedSlots = [{ date: null, time: null }, { date: null, time: null }, { date: null, time: null }];
            this.extraSlots = 0;
            this.counterReason = '';
            this.showDatePicker = [false, false, false];
            this.showTimePicker = [false, false, false];
        },
        submitProposal(requestId) {
            const slot1 = this.proposedSlots[0];
            if (!slot1.date || !slot1.time) {
                this.showErrorMessage('Por favor, selecione data e hora para a Opção 1');
                return;
            }

            this.currentAction = 'propose-' + requestId;
            this.actionLoading = true;

            const slot2 = this.extraSlots >= 1 ? this.proposedSlots[1] : null;
            const slot3 = this.extraSlots >= 2 ? this.proposedSlots[2] : null;

            this.ActionDoProposeRescheduleRequest({
                requestId,
                proposed_date:   slot1.date,
                proposed_time:   slot1.time,
                proposed_date_2: slot2?.date  || null,
                proposed_time_2: slot2?.time  || null,
                proposed_date_3: slot3?.date  || null,
                proposed_time_3: slot3?.time  || null,
                counter_reason:  this.counterReason || null,
            })
                .then(() => {
                    this.showSuccessMessage('Proposta enviada com sucesso!');
                    this.loadRequests();
                    this.resetPropose();
                })
                .catch(error => {
                    console.error('Error proposing new time:', error);
                    this.showErrorMessage('Erro ao enviar proposta');
                })
                .finally(() => {
                    this.actionLoading = false;
                    this.currentAction = null;
                });
        },
        formatDate(date) {
            return date ? dayjs(date).format('DD/MM/YYYY') : '';
        },
        formatDateTime(datetime) {
            return datetime ? dayjs(datetime).format('DD/MM/YYYY HH:mm') : '';
        },
        getCounterproposalStatusColor(request) {
            if (request.customers_session?.rescheduled) return 'success';
            if (request.customers_session?.finished) return 'error';
            return 'warning';
        },
        getCounterproposalStatusIcon(request) {
            if (request.customers_session?.rescheduled) return 'mdi-check-circle';
            if (request.customers_session?.finished) return 'mdi-close-circle';
            return 'mdi-clock-outline';
        },
        getCounterproposalStatusText(request) {
            if (request.customers_session?.rescheduled) return 'Aceita';
            if (request.customers_session?.finished) return 'Recusada/Finalizada';
            return 'Aguardando Resposta';
        },
        showSuccessMessage(message) {
            this.successMessage = message;
            this.showSuccess = true;
        },
        showErrorMessage(message) {
            this.errorMessage = message;
            this.showError = true;
        }
,
        // Tour helper methods
        addFakeRequestsForTour() {
            const fakeRequests = [
                // Pendente 1
                {
                    id: 'tour-fake-request-1',
                    status: 'pending',
                    reason: 'Tenho um compromisso importante neste horário e não conseguirei comparecer. Poderia remarcar para a tarde?',
                    new_date: '2026-01-25',
                    new_time: '15:00',
                    created_at: '2026-01-20T10:30:00',
                    customers_session: {
                        id: 'tour-fake-session-1',
                        session_date: '2026-01-23T10:00:00',
                        customer: {
                            id: 'tour-fake-customer-1',
                            name: 'João Silva'
                        },
                        service: {
                            id: 'tour-fake-service-1',
                            name: 'Psicoterapia Individual'
                        }
                    }
                },
                // Aceita 1
                {
                    id: 'tour-fake-request-2',
                    status: 'approved',
                    reason: 'Viagem de trabalho, preciso remarcar.',
                    new_date: '2026-01-18',
                    new_time: '14:00',
                    created_at: '2026-01-15T09:00:00',
                    updated_at: '2026-01-15T09:30:00',
                    customers_session: {
                        id: 'tour-fake-session-2',
                        session_date: '2026-01-16T10:00:00',
                        customer: {
                            id: 'tour-fake-customer-2',
                            name: 'Maria Santos'
                        },
                        service: {
                            id: 'tour-fake-service-1',
                            name: 'Psicoterapia Individual'
                        }
                    }
                },
                // Recusada 1
                {
                    id: 'tour-fake-request-3',
                    status: 'rejected',
                    reason: 'Consulta médica de emergência.',
                    new_date: '2026-01-19',
                    new_time: '16:00',
                    created_at: '2026-01-17T11:00:00',
                    updated_at: '2026-01-17T12:00:00',
                    customers_session: {
                        id: 'tour-fake-session-3',
                        session_date: '2026-01-19T10:00:00',
                        customer: {
                            id: 'tour-fake-customer-3',
                            name: 'Pedro Oliveira'
                        },
                        service: {
                            id: 'tour-fake-service-1',
                            name: 'Psicoterapia Individual'
                        }
                    }
                },
                // Contraproposta Aceita
                {
                    id: 'tour-fake-request-4',
                    status: 'proposed',
                    reason: 'Reunião de trabalho inesperada.',
                    new_date: '2026-01-22',
                    new_time: '16:00',
                    proposed_date: '2026-01-22',
                    proposed_time: '14:00',
                    created_at: '2026-01-18T08:00:00',
                    updated_at: '2026-01-18T09:00:00',
                    customers_session: {
                        id: 'tour-fake-session-4',
                        session_date: '2026-01-22T10:00:00',
                        rescheduled: true,
                        customer: {
                            id: 'tour-fake-customer-4',
                            name: 'Ana Costa'
                        },
                        service: {
                            id: 'tour-fake-service-1',
                            name: 'Psicoterapia Individual'
                        }
                    }
                },
                // Contraproposta Pendente
                {
                    id: 'tour-fake-request-5',
                    status: 'proposed',
                    reason: 'Problema familiar.',
                    new_date: '2026-01-24',
                    new_time: '11:00',
                    proposed_date: '2026-01-24',
                    proposed_time: '15:00',
                    created_at: '2026-01-19T14:00:00',
                    updated_at: '2026-01-19T15:00:00',
                    customers_session: {
                        id: 'tour-fake-session-5',
                        session_date: '2026-01-24T09:00:00',
                        rescheduled: false,
                        finished: false,
                        customer: {
                            id: 'tour-fake-customer-5',
                            name: 'Carlos Mendes'
                        },
                        service: {
                            id: 'tour-fake-service-1',
                            name: 'Psicoterapia Individual'
                        }
                    }
                }
            ];
            
            // Adicionar ao Vuex store
            this.$store.commit('schedule/SET_RESCHEDULE_REQUESTS', {
                data: [...this.allRequests, ...fakeRequests]
            });
            
            console.log('[Tour] Fake requests added');
        },
        removeFakeRequestsForTour() {
            // Remover fake requests do store
            const realRequests = this.allRequests.filter(r => !r.id.startsWith('tour-fake-'));
            
            this.$store.commit('schedule/SET_RESCHEDULE_REQUESTS', {
                data: realRequests
            });
            
            console.log('[Tour] Fake requests removed');
        },
        expandFirstPendingForTour() {
            this.$nextTick(() => {
                // Encontrar o primeiro expansion panel pendente
                const firstPanel = document.querySelector('#tour-pending-request .v-expansion-panel-header');
                if (firstPanel) {
                    firstPanel.click();
                    console.log('[Tour] First pending request expanded');
                }
            });
        }
    }
};
</script>
